// viewer.divider.js — draggable handle between the video panel and the map.
// Spec: docs/superpowers/specs/2026-05-26-resizable-video-map-divider-design.md

const MIN_PANEL_PX = 300;
const DIVIDER_PX = 10;

/**
 * Clamp a candidate map-panel width so both the map and the video panel stay
 * ≥ 300px. When the container is too narrow to fit both floors the lower
 * bound wins (map collapses last, video runs off-screen with overflow:hidden
 * — already the existing behaviour for the 300px min-width on the wrapper).
 *
 * @param {number} mapPx        Candidate map width in pixels.
 * @param {number} mainWidth    `.vmc-main` content-box width in pixels.
 * @returns {number}            Clamped width in pixels.
 */
export function clampMapPx(mapPx, mainWidth) {
  const width = Number.isFinite(mainWidth) && mainWidth > 0 ? mainWidth : 0;
  const upper = width - MIN_PANEL_PX - DIVIDER_PX;
  if (upper < MIN_PANEL_PX) return MIN_PANEL_PX;
  if (mapPx < MIN_PANEL_PX) return MIN_PANEL_PX;
  if (mapPx > upper) return upper;
  return mapPx;
}

/**
 * Parse a localStorage value into a sanitised percentage number.
 *
 * Strict: rejects unit suffixes, NaN, Infinity, and values outside [10, 90].
 * The band is wider than the runtime px clamp so the variable on first
 * mount is always *plausible*; the px clamp then re-narrows during drag.
 *
 * @param {string | null | undefined} raw
 * @returns {number | null}
 */
export function parsePersistedPct(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "string") return null;
  // Number() accepts "" → 0 and "  42  " → 42; we want strict numeric.
  if (raw.trim() === "") return null;
  if (!/^-?\d+(?:\.\d+)?$/.test(raw)) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  if (n < 10 || n > 90) return null;
  return n;
}

const STORAGE_KEY = "vmc.viewer.mapWidthPct";

/**
 * Mount the divider behaviour on an already-rendered `.vmc-viewer` tree.
 *
 * @param {HTMLElement} viewerEl  The `.vmc-viewer` root.
 * @param {{ map: { invalidateSize: (opts?: object) => void } }} deps
 * @returns {{ dispose: () => void }}
 */
export function mountDivider(viewerEl, { map }) {
  const divider = viewerEl.querySelector(".vmc-divider");
  if (divider === null) return { dispose: () => {} };
  const main = viewerEl.querySelector(".vmc-main");
  const mapWrap = viewerEl.querySelector(".vmc-map-wrapper");
  if (main === null || mapWrap === null) return { dispose: () => {} };

  let stored = null;
  try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* see Task 3 */ }
  const pct = parsePersistedPct(stored);
  if (pct !== null) {
    viewerEl.style.setProperty("--vmc-map-width", `${pct}%`);
    // The map was mounted against the default-width container and cached that
    // size; stretching it here without telling Leaflet leaves a dead strip on
    // the right where tiles never load and vectors never draw (Leaflet only
    // self-invalidates on window resize, and drags invalidate on pointerup).
    if (map && typeof map.invalidateSize === "function") {
      map.invalidateSize({ animate: false });
    }
  }

  let activePointerId = null;
  let rafId = 0;
  let lastClientX = 0;
  let moved = false;
  let lastPct = null;

  const applyMove = () => {
    rafId = 0;
    const mapRight = mapWrap.getBoundingClientRect().right;
    const mainWidth = main.clientWidth;
    const raw = mapRight - lastClientX - 5; // 5 = half divider width
    const clamped = clampMapPx(raw, mainWidth);
    const newPct = (clamped / mainWidth) * 100;
    if (Number.isFinite(newPct)) {
      viewerEl.style.setProperty("--vmc-map-width", `${newPct}%`);
      lastPct = newPct;
      moved = true;
    }
  };

  const onPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    activePointerId = event.pointerId;
    lastClientX = event.clientX;
    moved = false;
    lastPct = null;
    if (typeof divider.setPointerCapture === "function") {
      try { divider.setPointerCapture(event.pointerId); } catch { /* ignore */ }
    }
    viewerEl.classList.add("vmc-resizing");
  };

  const onPointerMove = (event) => {
    if (activePointerId === null || event.pointerId !== activePointerId) return;
    lastClientX = event.clientX;
    if (rafId !== 0) return;
    rafId = requestAnimationFrame(applyMove);
  };

  const onPointerUp = (event) => {
    if (activePointerId === null || event.pointerId !== activePointerId) return;
    activePointerId = null;
    if (rafId !== 0) { cancelAnimationFrame(rafId); rafId = 0; }
    viewerEl.classList.remove("vmc-resizing");
    if (moved && lastPct !== null) {
      try {
        localStorage.setItem(STORAGE_KEY, String(lastPct));
      } catch { /* localStorage blocked — drop persistence silently */ }
      if (map && typeof map.invalidateSize === "function") {
        map.invalidateSize({ animate: false });
      }
    }
    moved = false;
  };

  divider.addEventListener("pointerdown", onPointerDown);
  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerup", onPointerUp);
  window.addEventListener("pointercancel", onPointerUp);

  const onDblClick = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    viewerEl.style.removeProperty("--vmc-map-width");
    if (map && typeof map.invalidateSize === "function") {
      map.invalidateSize({ animate: false });
    }
  };
  divider.addEventListener("dblclick", onDblClick);

  return {
    dispose: () => {
      if (activePointerId !== null) {
        viewerEl.classList.remove("vmc-resizing");
        if (typeof divider.releasePointerCapture === "function") {
          try { divider.releasePointerCapture(activePointerId); } catch { /* ignore */ }
        }
        activePointerId = null;
      }
      divider.removeEventListener("pointerdown", onPointerDown);
      divider.removeEventListener("dblclick", onDblClick);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      if (rafId !== 0) cancelAnimationFrame(rafId);
    },
  };
}
